/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useState, useEffect, ChangeEvent } from "react";
import LabelGroup, { ROW_GROUP } from "../../../forms/elements/LabelGroup";
import { onIe } from "../../../language/browser";
import { getResource } from "../../../language/resources/getResource";
import {
  createEmptyEditableMaxDaysRule,
  EditableMaxDaysAccessRule,
} from "./maxDays";
import { Input } from "../../../forms/elements/Input";
import { ValidationIndicator } from "../validation/ValidationIndicator";

type Props = {
  resources: Record<string, any>;
  rule?: EditableMaxDaysAccessRule;
  onChange: (rule: EditableMaxDaysAccessRule | undefined) => void;
};

// Only allow non-negative whole numbers or blank
const MAX_DAYS_LENGTH = 4;
const MAX_DAYS_PATTERN = "^(0|[1-9]\\d{0,3})?$";
const MAX_DAYS_REGEX = new RegExp(MAX_DAYS_PATTERN);

const containerCss = css`
  margin-top: 10px;
  margin-bottom: 10px;
  display: flex;
`;

const inputCss = css`
  width: 70px;
  min-height: 35px !important;
  height: 35px;
  margin-left: 8px;
`;

const ieCheckbox = !onIe()
  ? ""
  : css`
      margin-top: -14px;
    `;


/**
 * Singleton access rule row (Max Days).
 *
 * This row edits a single Max Days rule. Blanking out the input
 * represents deleting the rule entirely.
 *
 * Editing model: local draft state for input only.
 *
 * The row maintains local draft state for the text input to allow
 * partial and invalid intermediate values while typing. Valid changes
 * are committed immediately to the parent via onChange.
 *
 * The parent editor owns the source of truth and computes persistence
 * diffs (created / updated / deleted).
 */
export function MaxDaysAccessRuleRow({ resources, rule, onChange }: Props) {
  const [maxDaysText, setMaxDaysText] = useState(
    rule?.numberOfDays?.toString() ?? "",
  );

  useEffect(() => {
    setMaxDaysText(rule?.numberOfDays?.toString() ?? "");
  }, [rule?.numberOfDays]);

  const isUnlimited = rule?.numberOfDays == null;

  const ruleIdTitle = rule?.liftAccessRuleId
    ? getResource(resources, "ACCESS_RULE_EDIT_RULE_ID", rule.liftAccessRuleId)
    : getResource(resources, "ACCESS_RULE_EDIT_NEW_RULE");

  const showZeroValidation = rule?.numberOfDays === 0;

  const updateRule = (patch: Partial<EditableMaxDaysAccessRule>) => {
    const nextRule: EditableMaxDaysAccessRule = rule
      ? { ...rule, ...patch }
      : { ...createEmptyEditableMaxDaysRule(), ...patch };

    onChange(nextRule);
  };

  const handleMaxDaysChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Allow only blank or non-negative integers
    if (!MAX_DAYS_REGEX.test(value)) return;

    setMaxDaysText(value);

    updateRule({
      numberOfDays: value === "" ? null : Number(value),
      // If days are cleared, shared usage should reset
      sharedUsage: value === "" ? false : (rule?.sharedUsage ?? false),
    });
  };

  const handleSharedUsageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!rule || isUnlimited) return;

    updateRule({
      sharedUsage: e.target.checked,
    });
  };

  return (
    <div css={containerCss}>
      <LabelGroup
        label={getResource(resources, "ACCESS_RULE_EDIT_MAX_DAYS")}
        info={getResource(resources, "ACCESS_RULE_EDIT_MAX_DAYS_INFO")}
        rootCss={ROW_GROUP}
      >
        <Input
          id="max-days-input"
          title={ruleIdTitle}
          type="text"
          css={inputCss}
          autoComplete="off"
          inputMode="numeric"
          maxLength={MAX_DAYS_LENGTH}
          pattern={MAX_DAYS_PATTERN}
          value={maxDaysText}
          onChange={handleMaxDaysChange}
        />
      </LabelGroup>

      <LabelGroup
        label={getResource(resources, "ACCESS_RULE_EDIT_MAX_DAYS_SHARED")}
        title={ruleIdTitle}
        rootCss={css`
          ${ROW_GROUP};
          margin-left: 20px;
        `}
      >
        <input
          id="shared-usage-checkbox"
          type="checkbox"
          css={css`
            ${onIe()
              ? ""
              : css`
                  margin-right: 8px !important;
                `};
            ${ieCheckbox};
            zoom: 1.25;
          `}
          checked={!isUnlimited && !!rule?.sharedUsage}
          onChange={handleSharedUsageChange}
          disabled={isUnlimited}
        />
      </LabelGroup>
      {
        <ValidationIndicator
          severity={"error"}
          message={getResource(resources, "ACCESS_RULE_VALIDATION_MAX_DAYS_MIN")}
          visible={!!showZeroValidation}
        />
      }
    </div>
  );
}
